/**
 * UI State Manager - Preserves UI state across page reloads (SW updates, etc.)
 * Saves and restores: current channel, panel states, scroll position
 */

interface UIState {
  currentChannelId: string | null;
  panelStates: {
    pinnedMessagesOpen: boolean;
    onlineUsersOpen: boolean;
    notificationSettingsOpen: boolean;
  };
  scrollPosition: {
    [channelId: string]: number;
  };
}

const UI_STATE_KEY = 'ui_state';

export const uiStateManager = {
  /**
   * Save current UI state to sessionStorage
   */
  saveState(state: Partial<UIState>): void {
    try {
      const existing = this.getState();
      const merged = { ...existing, ...state };
      sessionStorage.setItem(UI_STATE_KEY, JSON.stringify(merged));
    } catch (error) {
      console.warn('[UIStateManager] Failed to save UI state:', error);
    }
  },

  /**
   * Get saved UI state from sessionStorage
   */
  getState(): UIState {
    try {
      const saved = sessionStorage.getItem(UI_STATE_KEY);
      if (!saved) {
        return getDefaultState();
      }
      return JSON.parse(saved);
    } catch (error) {
      console.warn('[UIStateManager] Failed to parse UI state:', error);
      return getDefaultState();
    }
  },

  /**
   * Clear all saved UI state
   */
  clearState(): void {
    try {
      sessionStorage.removeItem(UI_STATE_KEY);
    } catch (error) {
      console.warn('[UIStateManager] Failed to clear UI state:', error);
    }
  },

  /**
   * Save current channel ID
   */
  setCurrentChannelId(channelId: string | null): void {
    this.saveState({ currentChannelId: channelId });
  },

  /**
   * Get saved channel ID
   */
  getCurrentChannelId(): string | null {
    return this.getState().currentChannelId;
  },

  /**
   * Save panel open/close states
   */
  setPanelStates(panels: Partial<UIState['panelStates']>): void {
    const state = this.getState();
    this.saveState({
      panelStates: { ...state.panelStates, ...panels },
    });
  },

  /**
   * Get panel states
   */
  getPanelStates(): UIState['panelStates'] {
    return this.getState().panelStates;
  },

  /**
   * Save scroll position for a channel
   */
  setScrollPosition(channelId: string, position: number): void {
    const state = this.getState();
    state.scrollPosition[channelId] = position;
    this.saveState({ scrollPosition: state.scrollPosition });
  },

  /**
   * Get saved scroll position for a channel
   */
  getScrollPosition(channelId: string): number | null {
    const position = this.getState().scrollPosition[channelId];
    return position !== undefined ? position : null;
  },

  /**
   * Clear scroll position for a channel
   */
  clearScrollPosition(channelId: string): void {
    const state = this.getState();
    delete state.scrollPosition[channelId];
    this.saveState({ scrollPosition: state.scrollPosition });
  },
};

function getDefaultState(): UIState {
  return {
    currentChannelId: null,
    panelStates: {
      pinnedMessagesOpen: false,
      onlineUsersOpen: true,
      notificationSettingsOpen: false,
    },
    scrollPosition: {},
  };
}
